import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { CreditCard, Keyboard, Lightbulb, LogOut, MessageSquarePlus, Moon, Settings, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { requestFeedback } from '@/lib/feedbackLauncher';

interface AccountMenuProps {
  email: string | undefined;
  onShowShortcuts: () => void;
  onSignOut: () => void;
}

/**
 * Der Avatar-Trigger im Header (Task 7). Seit WP6 (K29) mehr als E-Mail und
 * Abmelden: die Wege zu Einstellungen, Tarifen, Roadmap und Feedback stehen
 * hier, weil der Feedback-Knopf auf der Werkbank ausgeblendet ist (K07) und
 * der Theme-Knopf mobil aus der Leiste weicht (K30, Platzbilanz in
 * context/FeedSwitcher.tsx: Band bei 320 px 92 -> 108 px).
 */
export function AccountMenu({ email, onShowShortcuts, onSignOut }: AccountMenuProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const initial = (email?.[0] ?? '?').toUpperCase();
  const isDark = resolvedTheme === 'dark';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Mobil 44 px Tap-Flaeche, sichtbar bleibt der 30-px-Kreis (K30). */}
        <button
          type="button"
          aria-label={t('accountMenu.label')}
          className="group flex h-11 w-11 shrink-0 items-center justify-center rounded-full sm:h-[30px] sm:w-[30px]"
        >
          {/* Hausform aus button.tsx: hover:bg-primary-hover statt Opacity,
              sonst faellt bg-primary ueber Sand auf 4,65:1. */}
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary text-xs font-bold font-heading text-primary-foreground transition-colors group-hover:bg-primary-hover">
            {initial}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="truncate font-normal text-muted-foreground">{email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="mr-2 h-4 w-4" />
          {t('nav.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/pricing')}>
          <CreditCard className="mr-2 h-4 w-4" />
          {t('nav.plans')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/roadmap')}>
          <Lightbulb className="mr-2 h-4 w-4" />
          {t('nav.roadmap')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => requestFeedback('feedback')}>
          <MessageSquarePlus className="mr-2 h-4 w-4" />
          {t('accountMenu.feedback')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="sm:hidden" onSelect={() => setTheme(isDark ? 'light' : 'dark')}>
          {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
          {isDark ? t('accountMenu.themeLight') : t('accountMenu.themeDark')}
        </DropdownMenuItem>
        <DropdownMenuItem className="hidden sm:flex" onSelect={onShowShortcuts}>
          <Keyboard className="mr-2 h-4 w-4" />
          {t('accountMenu.shortcuts')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('actions.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
